import { clsx } from "clsx";
import { type FC, type ReactNode, cloneElement, useCallback, useEffect, useMemo, useState } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { useBlocker } from "react-router";
import { CSSTransition, TransitionGroup } from "react-transition-group";

import {
  ActionButton,
  ActionButtonIntent,
  AlertDialogComponent,
  HStack,
  LoadingSpinner,
  Modal,
  SymbolIcon,
} from "widgets";
import { useOverlay } from "widgets/hooks";
import { useExitPrompt } from "widgets/hooks";

import { SmartLink } from "../core/SmartLink.js";
import InformationPage from "../core/errors/InformationPage.js";
import { TooltipController } from "../core/overlays/components/OverlayControllers.js";
import { ModalControlledOverlay } from "../core/overlays/index.js";
import { type IWizardStep } from "./PlaybookWizardStep.js";
import { type IPlaybookPage } from "@/app-utils/playbook.js";
import { smartSplitToLines } from "@/app-utils/string.js";
import { selMessage } from "@/messages/message.js";
import { ModulesIcons, type SelectorIcon } from "@/settings/assets-settings.js";
import { RuntimeException } from "@/ui-modules/core/errors/RuntimeException.tsx";

interface IPlaybookWizardProps {
  title: string;
  suffix?: string;
  isInitializing?: boolean;
  helpLink: string;
  steps: Array<Partial<IPlaybookPage>>;
  mapStepToScreen: (page: Partial<IPlaybookPage>, screenIndex?: number) => IWizardStep;
  needsWideSpace?: (page: Partial<IPlaybookPage>) => boolean;
  isCurrentPageLooper?: (page: Partial<IPlaybookPage>) => boolean;
  className?: string; // useful for discriminating the wizards, so that we can apply different media queries depending on how we want the modal header to be adaptative in terms of screen size
  onClose: () => void;
  isLoading?: boolean;
  icon?: SelectorIcon;
  playbook?: boolean;
  configurationError?: ReactNode;
  updateConfigurationError?: (error: ReactNode) => void;
  onUseDemoData?: () => void;
  disableUseDemoDataButton?: boolean;
}

const PlaybookWizard: FC<IPlaybookWizardProps> = (props) => {
  const [currentStepIndex, setCurrentStepIndex] = useState<number>(1);
  const [wizardError, setWizardError] = useState<string>();
  const [isLoadingDemoData, setIsLoadingDemoData] = useState<boolean>(false);

  const next = useCallback(() => {
    if (currentStepIndex >= props.steps.length) {
      return props.onClose();
    }
    setCurrentStepIndex((currentStepIndex) => currentStepIndex + 1);
  }, [props.steps, currentStepIndex]);

  const prev = useCallback(
    (_event, forceStep?: number) => {
      const stepIndex = forceStep == undefined ? (currentStepIndex <= 1 ? 1 : currentStepIndex - 1) : forceStep;
      setCurrentStepIndex(stepIndex);
      setWizardError(undefined); // reset the wizard error when going back
    },
    [currentStepIndex],
  );

  const loadDemoData = useCallback(() => {
    setIsLoadingDemoData(true);
    setCurrentStepIndex(1);
    setWizardError(undefined);
    // force the step to mount again so that it can initialize from the redux state
    setTimeout(() => {
      props.onUseDemoData?.();
      setIsLoadingDemoData(false);
    }, 200);
  }, [props.onUseDemoData]);

  const splittedTitle = useMemo(
    () =>
      smartSplitToLines(props.title, 30).map((v, i, o) =>
        i === o.length ? (
          <span key={`${v}${i}`}>{v}</span>
        ) : (
          <span key={`${v}${i}`}>
            {v}
            <br />
          </span>
        ),
      ),
    [props.title],
  );

  useExitPrompt(true);
  const { state: exiConfirmationState, overlayProps: exiConfirmationOverlayProps } = useOverlay({ defaultOpen: false });
  const blocker = useBlocker(({ currentLocation, nextLocation }) => currentLocation.pathname !== nextLocation.pathname);
  useEffect(() => {
    if (blocker.state === "blocked") {
      exiConfirmationState.open();
    }
  }, [blocker.state]);

  const currentPlaybookPage: Partial<IPlaybookPage> = props.steps[currentStepIndex - 1];

  const isLoading = props.isLoading || props.isInitializing || isLoadingDemoData;
  const currentStep: IWizardStep | null = !isLoading
    ? props.mapStepToScreen(currentPlaybookPage, currentStepIndex - 1)
    : null;

  const wideVersion: boolean | undefined = props.steps.length <= 1 || props.needsWideSpace?.(currentPlaybookPage);
  const isCurrentPageLooper =
    typeof props.isCurrentPageLooper === "function" && currentPlaybookPage
      ? props.isCurrentPageLooper(currentPlaybookPage)
      : false;

  return (
    <>
      <Modal state={exiConfirmationState} {...exiConfirmationOverlayProps}>
        {/*@ts-ignore*/}
        <AlertDialogComponent
          closeFn={() => {
            blocker.reset?.();
            exiConfirmationState?.close?.();
          }}
          title={"Leave Playbook"}
          cancelButtonLabel={"Stay here"}
          mainButton={{
            label: "Discard and leave",
            intent: ActionButtonIntent.Danger,
            onPress: () => {
              blocker.proceed?.();
              exiConfirmationState?.close?.();
              props.onClose(); // leave the Playbook
            },
          }}
        >
          Are you sure you want to leave?
          <br />
          The information you entered will not be saved.
        </AlertDialogComponent>
      </Modal>

      <ModalControlledOverlay
        isKeyboardDismissDisabled
        isOpen
        close={() => {
          /*empty*/
        }}
      >
        {() => (
          <div
            className={clsx("playbook--modal_container", props.className, {
              "playbook--wide-form": wideVersion,
              "not-mention-playbook": !props.playbook,
            })}
          >
            <div className="playbook--modal" data-meta={currentStepIndex}>
              <div className="playbook--title_area">
                <img
                  width={20}
                  height={20}
                  src={props.icon?.small ?? ModulesIcons.DEFAULT.small}
                  alt=""
                  className="playbook--icon-mini"
                  aria-hidden="true"
                />
                <div className="playbook--title">
                  {splittedTitle}
                  <div className="playbook--suffix">{props.suffix}</div>
                </div>
              </div>
              <div className="playbook--icon_area playbook--hidden_sm" aria-hidden="true">
                <img width={140} height={80} src={props.icon?.large} alt="" className="playbook--icon" />
              </div>
              <div className={clsx("playbook--form_area", { "playbook--form_area--stack": isCurrentPageLooper })}>
                <ErrorBoundary FallbackComponent={RuntimeException}>
                  {props.configurationError ? (
                    <InformationPage
                      errorTitle={selMessage({ id: "playbook.execution.error" }).toString()}
                      noGoBackLink
                    >
                      <div>{props.configurationError}</div>
                    </InformationPage>
                  ) : isLoading ? (
                    <LoadingSpinner inCenter />
                  ) : (
                    <TransitionGroup style={{ height: "100%", width: "100%", position: "relative" }}>
                      <CSSTransition
                        key={`playbook-wizard-screen-${currentStepIndex}`}
                        classNames="playbook-wizard-screen-transition"
                        timeout={{ enter: 500, exit: 250 }}
                        unmountOnExit={true}
                        mountOnEnter={true}
                      >
                        <div style={{ height: "100%", width: "100%", position: "absolute" }}>
                          {currentStep &&
                            cloneElement(currentStep, {
                              backToPreviousStep: prev,
                              proceedWithNextStep: next,
                              closeWizard: props.onClose,
                              stepIndex: currentStepIndex,
                              wizardErrorMessage: wizardError,
                              updateWizardMessage: setWizardError,
                              updateConfigurationError: props.updateConfigurationError,
                            })}
                        </div>
                      </CSSTransition>
                    </TransitionGroup>
                  )}
                </ErrorBoundary>
              </div>
              <div className="playbook--summary_area">
                {props.onUseDemoData && (
                  <div className="playbook--demo-button">
                    <ActionButton
                      label={"Use Demo Data"}
                      onPress={loadDemoData}
                      isLoading={isLoading}
                      isDisabled={props.disableUseDemoDataButton}
                      tooltip={
                        props.disableUseDemoDataButton
                          ? "Using demo data is not possible when the Playbook was launched with a pre-defined set of samples"
                          : "Automatically populate the Playbook fields so it can be submitted with demo data"
                      }
                    />
                  </div>
                )}
                <div className="playbook--summary_title playbook--hidden_md">{props.title}</div>

                <div className="playbook--summaries">
                  {props.steps.map((playbookPage: IPlaybookPage, index) => {
                    const stepIndex = index + 1;

                    const title = playbookPage.title;
                    const isActive = stepIndex == currentStepIndex;
                    const isDone = stepIndex < currentStepIndex;

                    return (
                      <div
                        className={clsx("playbook--summaries_step", {
                          "playbook--summaries_step--active": isActive,
                          "playbook--summaries_step--done": isDone,
                        })}
                        key={`${title}-${stepIndex}`}
                      >
                        <span className="playbook--summaries_title">
                          <div>
                            {stepIndex}
                            <span className="playbook--hidden_md">. {title}</span>
                          </div>
                          {isDone && (
                            <div className="playbook--summaries_edit" onClick={(e) => prev(e, stepIndex)}>
                              Edit
                            </div>
                          )}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            <HStack className="playbook--actions" spacing={20}>
              {props.helpLink && (
                <TooltipController content={`${props.title} Help`}>
                  {(tooltipTriggerProps) => (
                    <SmartLink
                      to={props.helpLink}
                      forceExternal
                      // we cannot use the smartlink tooltip because there is
                      // no overflow on the help icon and the tooltip will not
                      // be visible in that case
                      // tooltip={`${this.props.title} Help`}
                      className="playbook--action"
                      rel="noopener noreferrer"
                      onPointerEnter={tooltipTriggerProps.onPointerEnter}
                      onPointerLeave={tooltipTriggerProps.onPointerLeave}
                      ref={tooltipTriggerProps.ref}
                      style={{ lineHeight: 0 }}
                    >
                      <SymbolIcon icon={"help"} />
                    </SmartLink>
                  )}
                </TooltipController>
              )}
              <div className="playbook--action" onClick={exiConfirmationState.open}>
                <SymbolIcon icon={"close"} />
                <span className="playbook--hidden_md">close</span>
              </div>
            </HStack>
          </div>
        )}
      </ModalControlledOverlay>
    </>
  );
};

export default PlaybookWizard;
